import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useReporterEmailVerification,
    VERIFICATION_CODE_LENGTH,
} from './useReporterEmailVerification';

import { PinField } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { InlineNotice } from '@components/atoms/InlineNotice/InlineNotice';

import { normaliseEmail } from '@utils/reporterEmail';

import {
    CodeFieldWrapper,
    CodeLabel,
    CodeRow,
    ConfirmationHint,
    ConfirmationWrapper,
    InlineAction,
    ResendHint,
    SlotBadge,
    SlotButton,
} from './ReporterEmailConfirmation.styled';

interface ReporterEmailConfirmationProps {
    /** The address to confirm. */
    email?: string | null;
    /** Whether the address currently entered passes the form's own validation. */
    isEmailValid: boolean;
    /**
     * Moves focus back to the email field so the reporter can correct the
     * address. Editing the address already resets the verification state.
     */
    onChangeAddress?: () => void;
    /**
     * Clears the address from the form - the way out of every dead end while
     * the address is optional. Absent when it is mandatory: there is no
     * removal exit there, by decision - the reporter confirms the address or
     * cannot file.
     */
    onRemoveAddress?: () => void;
}

/**
 * The confirmation control, attached to the email field: rendered inside
 * `AttachedField` (which also hosts the field itself), it puts the action in
 * a slot on the input's right edge - Send code, then Change while a code is
 * outstanding, then a Confirmed badge - and lays the code entry and its
 * hints directly under the field they belong to.
 */
export const ReporterEmailConfirmation = ({
    email,
    isEmailValid,
    onChangeAddress,
    onRemoveAddress,
}: ReporterEmailConfirmationProps) => {
    const { t } = useTranslation();

    const {
        attemptsRemaining,
        canResend,
        confirmCode,
        confirming,
        cooldownSeconds,
        problem,
        requestCode,
        requesting,
        status,
    } = useReporterEmailVerification(email);

    /**
     * `PinField` is uncontrolled, so the only way to empty it is to remount it. A fresh
     * code deserves a fresh field - leaving the old digits in place makes an
     * expired-code message look like it is about the code the reporter can still see.
     */
    const [fieldGeneration, setFieldGeneration] = useState(0);

    // Keyed on the normalised address, not the raw field: correcting the casing of an
    // address is not a different address, and remounting there would silently wipe
    // digits the reporter had already typed.
    const normalisedAddress = normaliseEmail(email);

    useEffect(() => {
        setFieldGeneration((previous) => previous + 1);
    }, [status, normalisedAddress]);

    /*
     * The slot overlays the input's right edge, so the geometry is measured,
     * not assumed: the input's own box gives the slot its top and height
     * (labels wrap in other languages and shift the input down), and the
     * slot's width becomes the input's extra right padding. All three land
     * as CSS variables on the hosting `AttachedField`. Observed rather than
     * measured once - the slot's text changes with state and language, and
     * the label can rewrap on resize. `isSlotRendered` is in the deps so the
     * cleanup clears the variables when the address is emptied or turns
     * invalid and the control stops rendering.
     */
    const slotRef = useRef<HTMLDivElement & HTMLButtonElement>(null);
    const isSlotRendered = !!email && isEmailValid;
    useEffect(() => {
        const slot = slotRef.current;
        const host = slot?.parentElement;
        // First input inside the wrapper = the email field; the code boxes
        // render after it.
        const input = host?.querySelector('input');
        if (!slot || !host || !input) return;

        const write = () => {
            const hostTop = host.getBoundingClientRect().top;
            const inputRect = input.getBoundingClientRect();
            host.style.setProperty(
                '--attached-slot-width',
                `${Math.ceil(slot.getBoundingClientRect().width)}px`,
            );
            // Inset by the 2px focus border so the slot never clips the
            // focus ring.
            host.style.setProperty(
                '--attached-slot-top',
                `${Math.round(inputRect.top - hostTop) + 2}px`,
            );
            host.style.setProperty(
                '--attached-slot-height',
                `${Math.max(Math.round(inputRect.height) - 4, 0)}px`,
            );
        };
        write();

        const clear = () => {
            host.style.removeProperty('--attached-slot-width');
            host.style.removeProperty('--attached-slot-top');
            host.style.removeProperty('--attached-slot-height');
        };

        if (typeof ResizeObserver === 'undefined') {
            return clear;
        }
        const observer = new ResizeObserver(write);
        observer.observe(slot);
        observer.observe(input);
        return () => {
            observer.disconnect();
            clear();
        };
    }, [status, isSlotRendered]);

    if (!email || !isEmailValid) return null;

    /*
     * Where there is no removal exit (the address is mandatory) the error
     * strings must not promise one - "remove the address and continue
     * without it" would be untrue there.
     */
    const hasRemovalExit = !!onRemoveAddress;

    const problemMessage = (() => {
        switch (problem) {
            case 'attemptsExhausted':
                return t(
                    hasRemovalExit
                        ? 'REPORTER_EMAIL_CONFIRM_ATTEMPTS_EXHAUSTED'
                        : 'REPORTER_EMAIL_CONFIRM_ATTEMPTS_EXHAUSTED_MANDATORY',
                );
            case 'expired':
                return t(
                    hasRemovalExit
                        ? 'REPORTER_EMAIL_CONFIRM_EXPIRED'
                        : 'REPORTER_EMAIL_CONFIRM_EXPIRED_MANDATORY',
                );
            case 'noTriesLeft':
                return t(
                    hasRemovalExit
                        ? 'REPORTER_EMAIL_CONFIRM_NO_TRIES_LEFT'
                        : 'REPORTER_EMAIL_CONFIRM_NO_TRIES_LEFT_MANDATORY',
                );
            case 'rateLimited':
                return t('REPORTER_EMAIL_CONFIRM_RATE_LIMITED', {
                    seconds: cooldownSeconds,
                });
            case 'unavailable':
                return t(
                    hasRemovalExit
                        ? 'REPORTER_EMAIL_CONFIRM_UNAVAILABLE'
                        : 'REPORTER_EMAIL_CONFIRM_UNAVAILABLE_MANDATORY',
                );
            case 'wrongCode':
                return attemptsRemaining === 1
                    ? t('REPORTER_EMAIL_CONFIRM_WRONG_CODE_ONE')
                    : t('REPORTER_EMAIL_CONFIRM_WRONG_CODE_MANY', {
                          // Not `count`: that key is reserved by i18next for plural
                          // resolution and would hijack this lookup per language.
                          attempts: attemptsRemaining ?? 0,
                      });
            default:
                return null;
        }
    })();

    const problemNotice = problemMessage && (
        <InlineNotice
            assertive
            testId="notice_email-confirm-problem"
            variant={'error'}
        >
            {problemMessage}
        </InlineNotice>
    );

    /*
     * Where the address is mandatory the errors above have no removal exit,
     * so a reporter whose mailbox cannot receive the code is at a genuine
     * dead end. Point them at the organisation's other reporting channels -
     * the portal holds no structured list of them, so the reference stays
     * generic. `noTriesLeft` is included because it is the exhausted case
     * where we merely lost track of the expiry.
     */
    const isMandatoryDeadEnd =
        !onRemoveAddress &&
        (problem === 'attemptsExhausted' ||
            problem === 'noTriesLeft' ||
            problem === 'unavailable');

    const mandatoryDeadEndHint = isMandatoryDeadEnd && (
        <ConfirmationHint data-testid="text_email-confirm-other-channels">
            {t('REPORTER_EMAIL_CONFIRM_OTHER_CHANNELS')}
        </ConfirmationHint>
    );

    if (status === 'verified') {
        /*
         * State lives on the control: the badge replaces the action in the
         * slot, no banner. What confirmation means for the reporter is said
         * by the field's own helper line, which the hosting form swaps.
         */
        return (
            <SlotBadge data-testid="notice_email-confirmed" ref={slotRef}>
                <IconManager icon={'SvgTickRounded'} size={14} />
                {t('REPORTER_EMAIL_CONFIRMED_BADGE')}
            </SlotBadge>
        );
    }

    const removeAddressHint = onRemoveAddress ? (
        <>
            {t('REPORTER_EMAIL_CONFIRM_REMOVE_PREFIX')}
            <InlineAction
                data-testid="button_remove-email"
                onClick={onRemoveAddress}
                type={'button'}
            >
                {t('REPORTER_EMAIL_CONFIRM_REMOVE_ACTION')}
            </InlineAction>
            {t('REPORTER_EMAIL_CONFIRM_REMOVE_SUFFIX')}
        </>
    ) : null;

    if (status === 'idle') {
        return (
            <>
                {/*
                 * Sending stays an explicit act: the slot swaps a full-width
                 * button for a field-attached one, and nothing leaves until
                 * the reporter presses it - an auto-sent code could alert a
                 * colleague whose address was typed by someone else.
                 */}
                <SlotButton
                    data-testid="button_send-email-code"
                    disabled={requesting || !canResend}
                    onClick={() => void requestCode()}
                    ref={slotRef}
                    // Sits inside the page's real <form>; without this it is a
                    // native submit button and requesting a code submits the form.
                    type={'button'}
                >
                    {t('REPORTER_EMAIL_SEND_CODE')}
                </SlotButton>
                {(problemNotice || mandatoryDeadEndHint) && (
                    <ConfirmationWrapper>
                        {problemNotice}
                        {mandatoryDeadEndHint}
                        {/*
                         * The way out is offered here too when one exists. If
                         * the very first request fails - the service is down,
                         * or the address is already rate limited - this branch
                         * is all the reporter ever sees. Where the address is
                         * mandatory there is no way out of the portal flow, by
                         * decision, so the error shows with the other-channels
                         * guidance above.
                         */}
                        {!!problemMessage && removeAddressHint && (
                            <ConfirmationHint>
                                {removeAddressHint}
                            </ConfirmationHint>
                        )}
                    </ConfirmationWrapper>
                )}
            </>
        );
    }

    return (
        <>
            {onChangeAddress && (
                <SlotButton
                    data-testid="button_change-email"
                    onClick={onChangeAddress}
                    ref={slotRef}
                    type={'button'}
                >
                    {t('REPORTER_EMAIL_CONFIRM_CHANGE_ADDRESS')}
                </SlotButton>
            )}
            <ConfirmationWrapper>
                <CodeFieldWrapper
                    aria-label={t('REPORTER_EMAIL_CONFIRM_CODE_GROUP_LABEL')}
                    role={'group'}
                >
                    <CodeLabel>
                        {t('REPORTER_EMAIL_CONFIRM_CODE_LABEL')}
                    </CodeLabel>
                    {/* Boxes and the resend affordance share a row, per the
                     * wireframe. */}
                    <CodeRow>
                        <PinField
                            autoComplete={'one-time-code'}
                            // Only the first box receives focus - the library
                            // applies this to index 0 alone. Focus moves here
                            // when the code-sent state (re)mounts, which is
                            // exactly when the reporter's next action is
                            // typing the code.
                            autoFocus
                            className="htmlToImageIgnore"
                            disabled={confirming}
                            error={!!problemMessage}
                            formatAriaLabel={(index, total) =>
                                t('REPORTER_EMAIL_CONFIRM_CODE_DIGIT_LABEL', {
                                    index,
                                    total,
                                })
                            }
                            inputMode={'numeric'}
                            key={fieldGeneration}
                            length={VERIFICATION_CODE_LENGTH}
                            onComplete={(value) => void confirmCode(value)}
                            spacing={8}
                            testId="input_email-confirmation-code"
                            validate={/^[0-9]$/}
                            xsSpacing={6}
                        />
                        <ResendHint>
                            {canResend ? (
                                <InlineAction
                                    data-testid="button_resend-email-code"
                                    onClick={() => void requestCode()}
                                    type={'button'}
                                >
                                    {t('REPORTER_EMAIL_CONFIRM_RESEND_NOW')}
                                </InlineAction>
                            ) : (
                                t('REPORTER_EMAIL_CONFIRM_RESEND_WAIT', {
                                    seconds: cooldownSeconds,
                                })
                            )}
                        </ResendHint>
                    </CodeRow>
                </CodeFieldWrapper>
                {problemNotice}
                {mandatoryDeadEndHint}
                {/* The address itself is visible in the field right above, so
                 * this line no longer repeats it. */}
                <ConfirmationHint>
                    {t('REPORTER_EMAIL_CONFIRM_SENT')}
                </ConfirmationHint>
                {removeAddressHint && (
                    <ConfirmationHint>{removeAddressHint}</ConfirmationHint>
                )}
            </ConfirmationWrapper>
        </>
    );
};
